import React from 'react';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SecurityIcon from '@mui/icons-material/Security';
import { useAuth } from './useAuth';
import { useDarkMode } from '../theme/DarkModeProvider';
import { useOptionalPermissionCenter } from '../contexts/PermissionCenterContext';

/**
 * AuthLayout - Standard layout wrapper with authentication handling
 *
 * Provides:
 * - AppBar with app title and login/logout button
 * - Authentication state management
 * - Loading state while checking auth
 * - Content area that only renders when authenticated
 * - Automatic dark-mode toggle when wrapped in a <DarkModeProvider>
 *
 * @param {Object} props
 * @param {string} props.title - Application title shown in AppBar
 * @param {React.ReactNode} props.children - Content to render when authenticated
 * @param {string} [props.appBarColor='white'] - AppBar color
 * @param {Object} [props.appBarSx] - Additional sx props for AppBar
 * @param {Object} [props.contentSx] - Additional sx props for content Box
 * @param {React.ReactNode} [props.headerContent] - Additional content in header (between title and auth button)
 * @param {boolean} [props.requireAuth=true] - Whether to require authentication to view content
 * @param {boolean} [props.showDarkModeToggle=true] - Show the dark-mode toggle when a DarkModeProvider is in the tree
 * @param {boolean} [props.showPermissionCenter=true] - Show permissions when a PermissionCenterProvider is in the tree
 *
 * @example
 * // Basic usage with dark mode
 * <DarkModeProvider appName="my-app">
 *   <AuthLayout title="My App">
 *     <Dashboard userId={userInfo?.userDetails} />
 *   </AuthLayout>
 * </DarkModeProvider>
 *
 * // With custom header content
 * <AuthLayout
 *   title="My App"
 *   headerContent={<Tabs value={tab} onChange={handleTabChange}>...</Tabs>}
 * >
 *   <Dashboard />
 * </AuthLayout>
 */
export const AuthLayout = ({
  title,
  children,
  appBarColor = 'white',
  appBarSx = {},
  contentSx = { p: 3 },
  headerContent = null,
  requireAuth = true,
  showDarkModeToggle = true,
  showPermissionCenter = true,
}) => {
  const { userInfo, isAuthenticated, isLoading, loginUrl, logoutUrl } = useAuth();
  const permissionCenter = useOptionalPermissionCenter();

  // Read dark-mode context if the app wrapped in a DarkModeProvider. Returns
  // null otherwise — no toggle renders, which is the right behavior for apps
  // that haven't opted in.
  const darkModeCtx = useDarkMode();
  const renderToggle = showDarkModeToggle && darkModeCtx !== null;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const showContent = !requireAuth || isAuthenticated;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AppBar position="static" color={appBarColor} sx={appBarSx}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: headerContent ? 0 : 1, mr: headerContent ? 2 : 0 }}>
            {title}
          </Typography>
          {headerContent && (
            <Box sx={{ flexGrow: 1 }}>
              {headerContent}
            </Box>
          )}
          {renderToggle && (
            <Tooltip title={darkModeCtx.darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton
                onClick={darkModeCtx.toggleDarkMode}
                size="small"
                aria-label="toggle dark mode"
                sx={{ mr: 1 }}
              >
                {darkModeCtx.darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
            </Tooltip>
          )}
          {isAuthenticated && showPermissionCenter && permissionCenter && (
            <Tooltip title="View permissions">
              <IconButton
                onClick={permissionCenter.openDrawer}
                size="small"
                aria-label="view permissions"
                sx={{ mr: 1 }}
              >
                <SecurityIcon />
              </IconButton>
            </Tooltip>
          )}
          {!isAuthenticated ? (
            <Button
              sx={{ backgroundColor: 'lightgrey', color: 'black', m: 1 }}
              href={loginUrl}
            >
              Log In
            </Button>
          ) : (
            <Button
              sx={{ backgroundColor: 'lightgrey', color: 'black', m: 1 }}
              href={logoutUrl}
            >
              Logout {userInfo?.userDetails}
            </Button>
          )}
        </Toolbar>
      </AppBar>
      {showContent && (
        <Box sx={contentSx}>
          {typeof children === 'function'
            ? children({ userInfo, isAuthenticated })
            : children}
        </Box>
      )}
    </Box>
  );
};

export default AuthLayout;
